import { FunctionalComponent, h } from '@stencil/core'
import { JSXBase } from '@stencil/core/internal'
import { noop } from 'lodash-es'

import { CallAiNoteTemplateBadgeType, ICallAiNotesBanner, ICallAiNoteTemplate } from '../types'

export const AiNoteTemplateBadge: FunctionalComponent<
  JSXBase.IntrinsicElements['span'] & { aiNoteTemplateBadge: CallAiNoteTemplateBadgeType; modified?: boolean }
> = ({ aiNoteTemplateBadge, class: className = '', modified = false }) => {
  if (modified) {
    return (
      <span class='flex gap-1 items-center py-4px px-8px text-xs font-semibold text-gray-50 bg-gray-600 rounded-full'>
        <ui-icon size='11px' class='text-gray-70' icon='gear' />
        Customized
      </span>
    )
  }
  let label = ''
  let colorClasses = ''
  let tooltip = ''
  if (aiNoteTemplateBadge === 'most_used') {
    label = 'Most Used'
    colorClasses = 'text-brand-base'
  } else {
    label = 'Free'
    colorClasses = 'text-accent-3'
    tooltip = 'This template will always be free to use'
  }
  return (
    <span
      class={`${className} px-2px text-xsmall font-bold uppercase group-hover:bg-opacity-10 rounded-sm ${colorClasses}`}
    >
      {label}
      {!!tooltip && <ui-infotip>{tooltip}</ui-infotip>}
    </span>
  )
}

export const AiNoteTemplateButton: FunctionalComponent<
  JSXBase.IntrinsicElements['button'] & { aiNoteTemplate: ICallAiNoteTemplate }
> = ({ aiNoteTemplate, onClick = noop }) => {
  return (
    <button
      class='group flex flex-shrink-0 gap-12px items-start p-12px w-48 h-14 bg-gray-900 hover:bg-brand-base hover:bg-opacity-20 rounded-md'
      onClick={onClick}
    >
      <ui-icon
        class='flex flex-col text-gray-200 group-hover:text-brand-base hover:text-opacity-80'
        icon={aiNoteTemplate.icon}
      />
      <div class='flex flex-col items-start'>
        <div class='flex justify-between items-center w-full text-gray-70 group-hover:text-brand-base'>
          <span class='text-small font-semibold'>{aiNoteTemplate.name}</span>
          {aiNoteTemplate.badge && <AiNoteTemplateBadge aiNoteTemplateBadge={aiNoteTemplate.badge} />}
        </div>
        <span class='flex pt-5px text-xsmall font-semibold text-left text-gray-100 group-hover:text-brand-base'>
          {aiNoteTemplate.description}
        </span>
      </div>
    </button>
  )
}

export const Banner: FunctionalComponent<{ banner: ICallAiNotesBanner } & JSXBase.IntrinsicElements['div']> = ({
  banner,
  class: className,
}) => {
  if (banner.type === 'premium_plan_user_free_preview_ended')
    return (
      <div
        class={`
        h-44px
        font-medium text-small leading-16px
        text-info-warn bg-info-warn bg-opacity-10 rounded-base
        pl-12px py-7px
        ${className}
      `}
      >
        You’re now seeing the free AI summary format since your free preview of Fathom Premium has ended.{' '}
        <a class='underline' href={banner.metadata.upgradeUrl} target='_blank'>
          Upgrade to Premium
        </a>{' '}
        to access advanced AI summaries.
      </div>
    )

  if (banner.type === 'team_edition_free_user_advanced_ai_preview') {
    return (
      <div
        class={`
        h-44px
        font-medium text-small leading-16px
        text-black bg-accent-3 rounded-base
        pl-12px py-7px
        ${className}
      `}
      >
        <b>FREE PREVIEW</b> - Enjoy our advanced templates for free for a limited time
        <a
          class='pl-15px font-extrabold underline'
          href='https://www.notion.so/fathom-video/Advanced-AI-Free-Preview-4d9256e528c54d029e5621d9ba20c268'
          target='_blank'
        >
          DETAILS
        </a>{' '}
      </div>
    )
  }

  return null
}
